import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { CardDisclosureTrigger } from '@/components/patterns/card-disclosure'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import {
  designCollectionsFilters,
  useCollections,
} from '@/lib/services/collections/collections.query'
import { useOrganization } from '@/lib/services/organizations/organizations.query'
import { locationFilters, useLocations } from '@/lib/services/locations/locations.query'
import { designProductsFilters, useProducts } from '@/lib/services/products/products.query'
import { useStoreThemes } from '@/lib/services/stores/stores.query'
import { isSameDayDeliveryPromotionEligible } from '@/lib/services/stores/stores.utils'
import { cn } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { useLocalStorage } from '@uidotdev/usehooks'
import { WebsiteEditorSplitLayout } from './WebsiteEditorSplitLayout'
import { useParams } from '@tanstack/react-router'
import {
  BookOpen,
  ChevronDown,
  CircleAlert,
  Images,
  LayoutGrid,
  MapPin,
  MessageCircleHeart,
  Palette,
  PanelTop,
  Settings2,
  Sparkles,
  Star,
  Wallpaper,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AboutUsPageForm from './AboutUsPageForm'
import BrandStyleForm from './BrandStyleForm'
import ContactUsPageForm from './ContactUsPageForm'
import FeaturedContentForm from './FeaturedContentForm'
import FooterSectionForm from './FooterSectionForm'
import GalleryPageForm from './GalleryPageForm'
import HeaderSectionForm from './HeaderSectionForm'
import HeroSectionForm from './HeroSectionForm'
import ProductPagesForm from './ProductPagesForm'
import ReviewsPageForm from './ReviewsPageForm'
import StorefrontPreviewFrame from './StorefrontPreviewFrame'
import { EMPTY_COLLECTION_MEDIA_DRAFTS, applyCollectionMediaDrafts } from './collectionMediaDrafts'
import { applyCollectionVisibilityUpdatesToPreview } from './storefrontPreviewConfig'
import { WEBSITE_EDITOR_TARGET_SECTION_KEY } from './websiteEditorTargetSection'

export { WebsiteEditorSplitLayout }

// Collection visibility may change while navigating elsewhere or in another admin tab.
const designCollectionQueryParams = {
  refetchOnMount: 'always',
  refetchOnWindowFocus: 'always',
}
const deliveryLocationFilters = locationFilters().get()

const sectionWorkspaceMap = {
  brand: 'brand-layout',
  header: 'brand-layout',
  footer: 'brand-layout',
  product: 'brand-layout',
  hero: 'pages-content',
  featured: 'pages-content',
  'featured-about': 'pages-content',
  'featured-gallery': 'pages-content',
  'featured-contact': 'pages-content',
  'featured-reviews': 'pages-content',
  about: 'pages-content',
  gallery: 'pages-content',
  contact: 'pages-content',
  reviews: 'pages-content',
}

const sectionPageMap = {
  hero: 'home',
  featured: 'home',
  'featured-about': 'about',
  'featured-gallery': 'gallery',
  'featured-contact': 'contact',
  'featured-reviews': 'reviews',
  about: 'about',
  gallery: 'gallery',
  contact: 'contact',
  reviews: 'reviews',
}

function normalizeOpenSections(value, fallbackId) {
  if (value === undefined || value === null) {
    return fallbackId ? [fallbackId] : []
  }

  if (!Array.isArray(value)) {
    return fallbackId ? [fallbackId] : []
  }

  return value.filter((entry) => typeof entry === 'string' && entry.trim() !== '')
}

export function WorkspaceSectionCard({
  id,
  icon: Icon,
  title,
  description,
  status,
  isOpen,
  onOpenChange,
  children,
}) {
  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <Card
        id={`website-editor-section-${id}`}
        className="@container/workspace-section w-full scroll-mt-2 overflow-hidden"
        selected={isOpen}
        size="flush"
      >
        <CollapsibleTrigger asChild>
          <CardDisclosureTrigger className="group flex items-center gap-4 px-4 py-3.5 md:px-5">
            <span
              className={cn(
                'inline-flex size-10 shrink-0 items-center justify-center rounded-xl border transition-colors',
                isOpen
                  ? 'border-primary/20 bg-primary/10 text-interaction'
                  : 'border-border/60 bg-muted/35 text-foreground'
              )}
            >
              <Icon className="size-[18px]" />
            </span>

            <span className="min-w-0 flex-1 space-y-0.5">
              <Typography variant="h5">{title}</Typography>
              <Typography variant="supporting" className="block max-w-2xl">
                {description}
              </Typography>
              {status ? (
                <SeasonalSectionStatusBadge
                  status={status}
                  aria-hidden="true"
                  className="mt-1 @[30rem]/workspace-section:hidden"
                />
              ) : null}
            </span>

            {status ? (
              <SeasonalSectionStatusBadge
                status={status}
                className="hidden max-w-44 @[30rem]/workspace-section:inline-flex"
              />
            ) : null}

            <span
              className={cn(
                'inline-flex size-9 shrink-0 items-center justify-center rounded-full border text-muted-foreground transition',
                isOpen
                  ? 'border-primary/20 bg-primary/10 text-interaction'
                  : 'border-border/70 bg-background group-hover:text-foreground'
              )}
            >
              <ChevronDown
                className={cn('size-[18px] transition-transform', isOpen && 'rotate-180')}
              />
            </span>
          </CardDisclosureTrigger>
        </CollapsibleTrigger>

        <CollapsibleContent className="overflow-hidden border-t border-border/60">
          <CardContent className="px-4 pb-5 pt-4 md:px-5 md:pb-6 md:pt-5">{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

function SeasonalSectionStatusBadge({ status, className, ...props }) {
  const Icon = status.icon
  const variant =
    status.tone === 'ready' ? 'success' : status.tone === 'attention' ? 'warning' : 'muted'

  return (
    <Badge variant={variant} size="status" className={className} {...props}>
      <Icon className="size-3.5" aria-hidden="true" />
      {status.label}
    </Badge>
  )
}

const workspaceCopy = {
  'brand-layout': {
    title: 'Brand & Layout',
    description:
      'Choose the colors, logos, navigation, and footer details your customers see across the storefront.',
    sections: [
      {
        id: 'brand',
        icon: Palette,
        title: 'Brand & Color',
        description:
          'Upload your logos, control how they appear, add a favicon, and choose your color palette.',
        component: BrandStyleForm,
        componentProps: {
          showThemePreview: false,
        },
      },
      {
        id: 'header',
        icon: PanelTop,
        title: 'Header & Navigation',
        description:
          'Adjust the announcement bar and menu links so shoppers can find what they need quickly.',
        component: HeaderSectionForm,
        componentProps: {
          showLogoSizePreview: false,
          showSameDayDeliverySettings: true,
        },
      },
      {
        id: 'footer',
        icon: MapPin,
        title: 'Store Details & Footer',
        description:
          'Control what shows in Visit Our Shop, plus the social links and footer details customers see at the bottom of every page.',
        component: FooterSectionForm,
      },
      {
        id: 'product',
        icon: Settings2,
        title: 'Product Disclaimer',
        description: 'Set the substitution message and choose which Product Types should show it.',
        component: ProductPagesForm,
      },
    ],
  },
  'pages-content': {
    title: 'Pages & Content',
    description:
      'Edit the words, photos, and featured sections that make the storefront feel like your shop.',
    sections: [
      {
        id: 'hero',
        icon: Sparkles,
        title: 'Homepage banner',
        description: 'Set the main homepage image, headline, supporting text, and button.',
        page: 'home',
        component: HeroSectionForm,
      },
      {
        id: 'featured',
        icon: Star,
        title: 'Featured Content',
        description:
          'Choose the product, collection, gallery, and Contact Us sections shoppers see below the main homepage content.',
        page: 'home',
        component: FeaturedContentForm,
        componentProps: {
          pageSlug: '/',
        },
      },
      {
        id: 'about',
        icon: BookOpen,
        title: 'About Us Story',
        description:
          'Tell shoppers who you are, what makes your design style special, and why they should trust your shop.',
        page: 'about',
        component: AboutUsPageForm,
      },
      {
        id: 'featured-about',
        icon: Star,
        title: 'Featured Content',
        description:
          'Add product, collection, gallery, or Contact Us sections below your About Us story and locations.',
        page: 'about',
        component: FeaturedContentForm,
        componentProps: {
          pageSlug: '/about-us',
        },
      },
      {
        id: 'gallery',
        icon: Images,
        title: 'Gallery Page',
        description:
          'Choose the heading, announcement link, and albums that show your recent work.',
        page: 'gallery',
        component: GalleryPageForm,
      },
      {
        id: 'featured-gallery',
        icon: Star,
        title: 'Featured Content',
        description:
          'Add product, collection, or Contact Us sections below your gallery page content.',
        page: 'gallery',
        component: FeaturedContentForm,
        componentProps: {
          pageSlug: '/gallery',
        },
      },
      {
        id: 'contact',
        icon: MessageCircleHeart,
        title: 'Contact Page',
        description:
          'Guide customers on how to reach out, ask questions, and send event or wedding requests.',
        page: 'contact',
        component: ContactUsPageForm,
      },
      {
        id: 'featured-contact',
        icon: Star,
        title: 'Featured Content',
        description: 'Add product, collection, or gallery sections below your Contact form.',
        page: 'contact',
        component: FeaturedContentForm,
        componentProps: {
          pageSlug: '/contact-us',
        },
      },
      {
        id: 'reviews',
        icon: Star,
        title: 'Reviews Page',
        description:
          'Edit the heading and intro customers see before reading and submitting reviews.',
        page: 'reviews',
        component: ReviewsPageForm,
      },
      {
        id: 'featured-reviews',
        icon: Star,
        title: 'Featured Content',
        description: 'Add product, collection, gallery, or Contact Us sections below your reviews.',
        page: 'reviews',
        component: FeaturedContentForm,
        componentProps: {
          pageSlug: '/reviews',
        },
      },
    ],
  },
}

const pageSettingsTabs = [
  { id: 'home', label: 'Home', icon: LayoutGrid },
  { id: 'about', label: 'About', icon: BookOpen },
  { id: 'gallery', label: 'Gallery', icon: Images },
  { id: 'contact', label: 'Contact', icon: MessageCircleHeart },
  { id: 'reviews', label: 'Reviews', icon: Star },
]

const builderWorkspaceTabs = [
  { id: 'brand-layout', label: 'Brand & Layout', icon: Palette },
  { id: 'pages-content', label: 'Pages & Content', icon: BookOpen },
]

function scrollSettingsPanelToTop(container) {
  const viewport = container?.querySelector('[data-slot="scroll-area-viewport"]')
  viewport?.scrollTo({ top: 0, left: 0 })
}

function scrollSettingsPanelToSection(container, sectionId) {
  const viewport = container?.querySelector('[data-slot="scroll-area-viewport"]')
  const element = sectionId ? document.getElementById(`website-editor-section-${sectionId}`) : null

  if (!viewport || !element) {
    return
  }

  const viewportRect = viewport.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()
  const nextScrollTop = viewport.scrollTop + elementRect.top - viewportRect.top - 8

  viewport.scrollTo({ top: Math.max(0, nextScrollTop), left: 0, behavior: 'smooth' })
}

function BuilderWorkspaceTabs({ workspaceSection, onWorkspaceSectionChange }) {
  return (
    <Tabs
      value={workspaceSection}
      onValueChange={onWorkspaceSectionChange}
      className="min-w-0 gap-0"
      size="comfortable"
    >
      <TabsList aria-label="Website builder sections" className="no-scrollbar">
        {builderWorkspaceTabs.map((tab) => {
          const Icon = tab.icon

          return (
            <TabsTrigger key={tab.id} value={tab.id}>
              <Icon className="size-4 shrink-0" aria-hidden="true" />
              {tab.label}
            </TabsTrigger>
          )
        })}
      </TabsList>
    </Tabs>
  )
}

function PageSettingsTabs({ previewPage, onPreviewPageChange }) {
  return (
    <div className="w-full rounded-xl border border-border bg-card p-4">
      <div className="min-w-0">
        <Typography variant="labelStrong" className="block">
          Page settings
        </Typography>
        <Typography variant="caption" className="block">
          Choose which website page you want to edit.
        </Typography>
      </div>

      <Tabs value={previewPage} onValueChange={onPreviewPageChange} className="mt-3 min-w-0 gap-0">
        <TabsList aria-label="Choose a website page to edit" className="no-scrollbar">
          {pageSettingsTabs.map((page) => {
            const Icon = page.icon

            return (
              <TabsTrigger key={page.id} value={page.id}>
                <Icon className="size-3.5" />
                {page.label}
              </TabsTrigger>
            )
          })}
        </TabsList>
      </Tabs>
    </div>
  )
}

function useStorefrontPreviewDesignValues(form) {
  // Schedules decide when a design is active, not how it renders.
  const id = useStore(form.store, (state) => state.values.id)
  const organizationId = useStore(form.store, (state) => state.values.organization_id)
  const isDefault = useStore(form.store, (state) => state.values.is_default)
  const name = useStore(form.store, (state) => state.values.name)
  const status = useStore(form.store, (state) => state.values.status)
  const createdFrom = useStore(form.store, (state) => state.values.created_from)
  const sectionOverrides = useStore(form.store, (state) => state.values.section_overrides)
  const seasonal = useStore(form.store, (state) => state.values.seasonal)
  const brand = useStore(form.store, (state) => state.values.brand)
  const pages = useStore(form.store, (state) => state.values.pages)
  const updatedAt = useStore(form.store, (state) => state.values.updated_at)

  return useMemo(
    () => ({
      id,
      organization_id: organizationId,
      is_default: isDefault,
      name,
      status,
      created_from: createdFrom,
      section_overrides: sectionOverrides,
      seasonal,
      brand,
      pages,
      updated_at: updatedAt,
    }),
    [
      brand,
      createdFrom,
      id,
      isDefault,
      name,
      organizationId,
      pages,
      seasonal,
      sectionOverrides,
      status,
      updatedAt,
    ]
  )
}

export function StorefrontPreviewFormFrame({ form, locations = [], ...props }) {
  const design = useStorefrontPreviewDesignValues(form)
  const collectionVisibilityUpdates = useStore(
    form.store,
    (state) => state.values.collection_visibility_updates
  )
  const sameDayDeliveryPromotionEnabled = useStore(
    form.store,
    (state) => state.values.store_settings?.same_day_delivery_promotion_enabled
  )
  const sameDayDeliveryPromotionEligible = isSameDayDeliveryPromotionEligible(locations)
  const previewCollections = applyCollectionVisibilityUpdatesToPreview(
    props.collections,
    collectionVisibilityUpdates
  )

  return (
    <StorefrontPreviewFrame
      {...props}
      collections={previewCollections}
      design={design}
      sameDayDeliveryPromotionEnabled={
        sameDayDeliveryPromotionEligible && sameDayDeliveryPromotionEnabled
      }
    />
  )
}

export default function WebsiteDesignWorkspace({
  form,
  workspaceSection,
  onWorkspaceSectionChange = null,
  getPersistedGalleryValues = null,
  onRebasePersistedGalleryValues = null,
  collectionMediaDraftController = null,
}) {
  const { orgId } = useParams({ strict: false })
  const { data: organization } = useOrganization(orgId)
  const { data: locations = [] } = useLocations({ filters: deliveryLocationFilters })
  const { data: themes = [] } = useStoreThemes({ organizationId: orgId })
  const { data: collections = [] } = useCollections({
    organizationId: orgId,
    filters: designCollectionsFilters,
    params: designCollectionQueryParams,
  })
  const collectionMediaDrafts =
    collectionMediaDraftController?.drafts ?? EMPTY_COLLECTION_MEDIA_DRAFTS
  const collectionsWithMediaDrafts = useMemo(
    () => applyCollectionMediaDrafts(collections, collectionMediaDrafts),
    [collectionMediaDrafts, collections]
  )
  const { data: products = [] } = useProducts({
    organizationId: orgId,
    filters: designProductsFilters,
  })
  const [previewPage, setPreviewPage] = useState(() => {
    if (typeof window === 'undefined') {
      return 'home'
    }

    const targetSectionId = window.sessionStorage.getItem(WEBSITE_EDITOR_TARGET_SECTION_KEY)
    const targetWorkspace = targetSectionId ? sectionWorkspaceMap[targetSectionId] : null
    const targetPage = targetSectionId ? sectionPageMap[targetSectionId] : null

    return targetWorkspace === 'pages-content' && targetPage ? targetPage : 'home'
  })
  const [previewDevice, setPreviewDevice] = useState('desktop')
  const pendingSectionIdRef = useRef(null)
  const settingsPanelRef = useRef(null)
  const workspace = workspaceCopy[workspaceSection] ?? workspaceCopy['brand-layout']
  const isPagesWorkspace = workspaceSection === 'pages-content'
  const visibleSections = useMemo(
    () =>
      isPagesWorkspace
        ? workspace.sections.filter((section) => section.page === previewPage)
        : workspace.sections,
    [isPagesWorkspace, previewPage, workspace.sections]
  )
  const visibleSectionIds = useMemo(
    () => new Set(visibleSections.map((section) => section.id)),
    [visibleSections]
  )
  const initialOpenSectionId = visibleSections[0]?.id ?? null
  const [storedOpenSections, setStoredOpenSections] = useLocalStorage(
    `website-editor-open-sections:${workspaceSection}:${isPagesWorkspace ? previewPage : 'all'}`,
    initialOpenSectionId ? [initialOpenSectionId] : []
  )
  const openSections = useMemo(
    () =>
      normalizeOpenSections(storedOpenSections, initialOpenSectionId).filter((sectionId) =>
        visibleSectionIds.has(sectionId)
      ),
    [initialOpenSectionId, storedOpenSections, visibleSectionIds]
  )

  const setSectionOpen = useCallback(
    (sectionId, nextOpen) => {
      setStoredOpenSections((currentValue) => {
        const currentSections = normalizeOpenSections(currentValue, initialOpenSectionId)

        if (nextOpen) {
          return currentSections.includes(sectionId)
            ? currentSections
            : [...currentSections, sectionId]
        }

        return currentSections.filter((entry) => entry !== sectionId)
      })
    },
    [initialOpenSectionId, setStoredOpenSections]
  )

  const focusSection = useCallback(
    (sectionId) => {
      requestAnimationFrame(() => {
        setStoredOpenSections([sectionId])
      })

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollSettingsPanelToSection(settingsPanelRef.current, sectionId)
        })
      })
    },
    [setStoredOpenSections]
  )

  const openSection = useCallback(
    (sectionId) => {
      setSectionOpen(sectionId, true)

      requestAnimationFrame(() => {
        scrollSettingsPanelToSection(settingsPanelRef.current, sectionId)
      })
    },
    [setSectionOpen]
  )

  const scrollToSection = (sectionId) => {
    const targetWorkspace = sectionWorkspaceMap[sectionId]

    if (!targetWorkspace) {
      return
    }

    if (targetWorkspace !== workspaceSection) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(WEBSITE_EDITOR_TARGET_SECTION_KEY, sectionId)
      }

      onWorkspaceSectionChange?.(targetWorkspace)
      return
    }

    const targetPage = sectionPageMap[sectionId]

    if (targetWorkspace === 'pages-content' && targetPage && targetPage !== previewPage) {
      pendingSectionIdRef.current = sectionId
      setPreviewPage(targetPage)
      return
    }

    openSection(sectionId)
  }

  const resetSettingsPanelScroll = useCallback(() => {
    scrollSettingsPanelToTop(settingsPanelRef.current)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const targetSectionId = window.sessionStorage.getItem(WEBSITE_EDITOR_TARGET_SECTION_KEY)
    const targetWorkspace = targetSectionId ? sectionWorkspaceMap[targetSectionId] : null

    if (targetWorkspace === workspaceSection) {
      return
    }

    const animationFrame = window.requestAnimationFrame(resetSettingsPanelScroll)

    return () => window.cancelAnimationFrame(animationFrame)
  }, [resetSettingsPanelScroll, workspaceSection])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const targetSectionId = window.sessionStorage.getItem(WEBSITE_EDITOR_TARGET_SECTION_KEY)
    const targetWorkspace = targetSectionId ? sectionWorkspaceMap[targetSectionId] : null

    if (!targetSectionId || targetWorkspace !== workspaceSection) {
      return
    }

    window.sessionStorage.removeItem(WEBSITE_EDITOR_TARGET_SECTION_KEY)

    const targetPage = sectionPageMap[targetSectionId]

    if (targetWorkspace === 'pages-content' && targetPage && targetPage !== previewPage) {
      pendingSectionIdRef.current = targetSectionId
      requestAnimationFrame(() => {
        setPreviewPage(targetPage)
      })
      return
    }

    focusSection(targetSectionId)
  }, [focusSection, previewPage, workspaceSection])

  useEffect(() => {
    const pendingSectionId = pendingSectionIdRef.current

    if (!pendingSectionId) {
      return
    }

    const targetWorkspace = sectionWorkspaceMap[pendingSectionId]
    const targetPage = sectionPageMap[pendingSectionId]

    if (targetWorkspace !== workspaceSection) {
      return
    }

    if (targetWorkspace === 'pages-content' && targetPage && targetPage !== previewPage) {
      return
    }

    pendingSectionIdRef.current = null
    focusSection(pendingSectionId)
  }, [focusSection, previewPage, workspaceSection])

  const settingsPanel = (
    <div
      ref={settingsPanelRef}
      className="flex h-full min-h-0 min-w-0 flex-col gap-2 overflow-hidden"
    >
      <BuilderWorkspaceTabs
        workspaceSection={workspaceSection}
        onWorkspaceSectionChange={onWorkspaceSectionChange}
      />

      {isPagesWorkspace ? (
        <PageSettingsTabs
          previewPage={previewPage}
          onPreviewPageChange={(nextPage) => setPreviewPage(nextPage)}
        />
      ) : null}

      <ScrollArea data-website-editor-scroll-panel className="h-full min-h-0 flex-1">
        <div className="space-y-4 pb-56 pt-2">
          {visibleSections.length > 0 ? (
            visibleSections.map((section) => {
              const Component = section.component
              const isOpen = openSections.includes(section.id)
              const componentProps = {
                ...(section.componentProps ?? {}),
                ...(Component === FeaturedContentForm
                  ? {
                      collectionsOverride: collectionsWithMediaDrafts,
                      collectionMediaDraftController,
                    }
                  : {}),
                ...(section.id === 'gallery'
                  ? {
                      getPersistedValues: getPersistedGalleryValues,
                      onRebasePersistedValues: onRebasePersistedGalleryValues,
                    }
                  : {}),
              }

              return (
                <WorkspaceSectionCard
                  key={section.id}
                  id={section.id}
                  icon={section.icon}
                  title={section.title}
                  description={section.description}
                  isOpen={isOpen}
                  onOpenChange={(nextOpen) => setSectionOpen(section.id, nextOpen)}
                >
                  <Component {...componentProps} />
                </WorkspaceSectionCard>
              )
            })
          ) : (
            <Card>
              <CardContent className="space-y-2 px-5 py-6">
                <Typography variant="h4">No page settings here</Typography>
                <Typography variant="supporting" className="block">
                  This page is powered by your product catalog or another storefront area. Use the
                  page buttons above to edit Home, About, Gallery, or Contact content.
                </Typography>
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>
    </div>
  )

  const previewPanel = (
    <div className="h-full min-h-0 overflow-hidden">
      <StorefrontPreviewFormFrame
        form={form}
        locations={locations}
        organization={organization}
        themes={themes}
        collections={collectionsWithMediaDrafts}
        products={products}
        previewPage={previewPage}
        onPreviewPageChange={setPreviewPage}
        previewDevice={previewDevice}
        onPreviewDeviceChange={setPreviewDevice}
        onSectionRequest={scrollToSection}
        className="h-full min-h-0"
        viewportClassName="h-full min-h-0"
      />
    </div>
  )

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" data-website-editor-workspace>
      <WebsiteEditorSplitLayout
        settingsPanel={settingsPanel}
        previewPanel={previewPanel}
        autoSaveId={`website-editor-${workspaceSection}-panels`}
      />
    </div>
  )
}
